import { useCallback, useState } from 'react';
import ReactGa from 'react-ga4';
import { useTranslation } from 'react-i18next';

import { ASSETS_BASE_URL, NTEASER_TTCC_PDF } from '@/config';
import { Button, Drawer } from '@/components';
import { useOpportunityDetails } from '@/features/opportunities/hooks/use-opportunity-details';

import { useOnCreateOffer, useOnUpdateOffer, useUploadOffer } from '../../hooks';

import { CreateOfferForm, CreateOfferFormFields } from './form';

import { useGetCurrentUserQuery } from '@/features/users';
import { triggerFileDownload, TriggerFileDownloadProps } from '../../helpers/trigger-ttcc-pdf-download';
import { europeanFormatNumber } from '@/helpers';
import { useDevelopmentStatus, useSaleOptions, useTechnologies } from '@/hooks';
import { useLocation } from '@/features/projects';
import { TechnologyType } from '@/features/projects/types';
import { FileSchema } from '@/features/files';

export type CreateOfferDrawerProps = {
  readonly id: string;
  readonly isOpen: boolean;
  readonly setIsOpen: (state: boolean) => void;
};

const PDF_TTCC_URL = `${ASSETS_BASE_URL}/${NTEASER_TTCC_PDF}`;

export function CreateOfferDrawer({ id, isOpen, setIsOpen }: CreateOfferDrawerProps) {
  const { normalizedOpportunity } = useOpportunityDetails(id);
  const { t } = useTranslation(['offers']);
  const { data: user } = useGetCurrentUserQuery();
  const { map: mapSaleOption } = useSaleOptions();
  const { kv: transformDevelopmentStatus } = useDevelopmentStatus();
  const location = useLocation({ location: normalizedOpportunity ? normalizedOpportunity.location : [] });
  const { map: mapTechnology } = useTechnologies();
  const { uploadSubmittedEoiFile } = useUploadOffer();
  const { updateOfferGeneratedPdf } = useOnUpdateOffer({ id });

  const [file, setFile] = useState<FileSchema[]>([]);
  const [eoiFile, setEoiFile] = useState<FileSchema[]>([]);
  const [corporateFile, setCorporateFile] = useState<FileSchema[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const onCloseSuccessSubmit = useCallback(() => {
    if (!normalizedOpportunity || !user) return;

    ReactGa.event({
      category: 'form_submitted',
      action: `New Offer`,
      label: `Offer Created on "${normalizedOpportunity.name}" opportunity`,
    });

    setIsOpen(false);
  }, [setIsOpen, normalizedOpportunity, user]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    if (!normalizedOpportunity) return;

    ReactGa.event({
      category: 'form_closed',
      action: `Offer creation proccess cancelled`,
      label: `User has abandoned the offer creation process on "${normalizedOpportunity.name}" opportunity`,
    });
  }, [setIsOpen, normalizedOpportunity]);

  const onSubmit = useOnCreateOffer({ id, onClose: onCloseSuccessSubmit, user });

  const handleSubmit = async (values: CreateOfferFormFields) => {
    if (normalizedOpportunity && user) {
      setIsUploading(true);

      const name = normalizedOpportunity.name;
      const PDF_TERMS_NAME = `nTeaser-Expression_of_interest_for_${name.trim().replace(/ /g, '_')}.pdf`;
      const technologies = normalizedOpportunity.technologies.types
        .map((tech: TechnologyType) => mapTechnology(tech))
        .join(', ');
      const installedCapacity = normalizedOpportunity.hasPV
        ? `${europeanFormatNumber(normalizedOpportunity.technologies.capacity)} MWp`
        : `${europeanFormatNumber(normalizedOpportunity.technologies.capacity)} MW`;
      const saleOptions = (normalizedOpportunity?.options ?? []).map(mapSaleOption).join(', ');
      const developmentStatus = normalizedOpportunity.technologies.state.list
        .map((status) => transformDevelopmentStatus[status])
        .join(', ');
      const corporateFileTitle = corporateFile[0]?.title ?? null;
      const eoiFileTitle = eoiFile[0]?.title ?? null;
      const fileTitle = file[0]?.title ?? null;

      const fileProps: TriggerFileDownloadProps = {
        url: PDF_TTCC_URL,
        filename: PDF_TERMS_NAME,
        data: values,
        user,
        name,
        technologies,
        installedCapacity,
        saleOptions,
        developmentStatus,
        location,
        corporateFileTitle,
        eoiFileTitle,
        fileTitle,
      };

      try {
        const createdOffer: any = await onSubmit({
          ...values,
          is_submitted_eoi_file_generated: false,
        });
        try {
          const pdfFile = await triggerFileDownload(fileProps);
          const uploadedFile = await uploadSubmittedEoiFile(pdfFile);

          const offerId = createdOffer?.data?.id ?? createdOffer?.id;
          if (!offerId) {
            console.error('No offer id available', createdOffer);
            setIsUploading(false);
            return;
          }
          updateOfferGeneratedPdf({
            id: createdOffer.data.id,
            submitted_eoi_file: uploadedFile?.id,
            is_submitted_eoi_file_generated: true,
          });
          setIsUploading(false);
        } catch (pdfError) {
          console.error('Error generating or uploading EOI file:', pdfError);
          updateOfferGeneratedPdf({
            id: createdOffer.data.id,
            is_submitted_eoi_file_generated: false,
          });
          setIsUploading(false);
        }
      } catch (error) {
        console.error('Error creating offer:', error);
        setIsUploading(false);
      }
    }
  };

  const footer = (
    <>
      <Button size="lg" color="neutral" onClick={onClose}>
        {t('Cancel')}
      </Button>
      <Button type="submit" form="create-offer-form" size="lg" color="primary">
        {t('Accept & Submit')}
      </Button>
    </>
  );

  if (!normalizedOpportunity) return null;

  return (
    <Drawer title={t('Submit Your Expression of Interest')} open={isOpen} onClose={onClose} footer={footer}>
      <CreateOfferForm
        id="create-offer-form"
        onSubmit={handleSubmit}
        file={file}
        onFileChange={setFile}
        corporateFile={corporateFile}
        onCorporateFileChange={setCorporateFile}
        eoiFile={eoiFile}
        onEoiFileChange={setEoiFile}
      />
    </Drawer>
  );
}
